import React, { useState, useEffect } from "react";
import { func, number, oneOfType, shape, string, arrayOf, bool } from "prop-types";
import * as config from "util/config";
import * as i18nUtils from "util/i18n";
import classNames from "classnames";
import I18n from "pages/_components/I18n";
import Button from "pages/_components/Button";
import CustomText from "pages/_components/CustomText/CustomText";
import { useDispatch, useSelector } from "react-redux";
import { actions as notificationActions } from "reducers/notification";
import { actions as comexActions } from "reducers/comex";
import { actions as thirdPaymentActions } from "reducers/thirdPayment";
import FieldError from "pages/_components/fields/FieldError";
import Spinner from "pages/_components/WidgetLoading";
import { isMobile } from "react-device-detect";

const DocumentUploader = ({
    className,
    label,
    documentCode,
    form,
    field,
    onChange,
    defaultDocument,
    isValid,
    overrideDocumentName,
    allowedTypes,
    multiUploaderId,
    withDocumentName,
    idForm,
    externalUploadError,
    totalRecords,
    totalAmount,
    environmentCuit,
    fileId,
}) => {
    const dispatch = useDispatch();
    const { documentToUploadRequest = {}, comexId = null } = useSelector((state) => state.accounts);

    const {
        id: documentToUploadRequestId,
        status: documentToUploadRequestStatus,
        value: documentToUploadRequestValue,
        error: documentToUploadRequestError,
    } = documentToUploadRequest;

    const documentInputRef = React.useRef(null);
    const uploaderRequestId = multiUploaderId || documentCode;
    const [document, setDocument] = useState(null);
    const [documentUploading, setDocumentUploading] = useState(null);
    const [documentIsUploading, setDocumentIsUploading] = useState(false);
    const [hasInvalidType, setHasInvalidType] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [alreadyResetDocument, setAlreadyResetDocument] = useState(false);
    const [lastDocumentToUploadRequest, setLastDocumentToUploadRequest] = useState({
        status: "INITIAL",
        value: "",
        error: null,
    });
    const {
        status: lastDocumentToUploadRequestStatus,
        value: lastDocumentToUploadRequestValue,
        error: lastDocumentToUploadRequestError,
    } = lastDocumentToUploadRequest;

    const MAX_FILE_SIZE = config.getInteger("directDebit.maxFileSize", 5242880); // 5MB por defecto

    const handleDelete = () => {
        setDocument({});
        setDocumentIsUploading(false);
        setLastDocumentToUploadRequest({
            status: "INITIAL",
            value: "",
            error: null,
        });
        form.setFieldValue(field.name, "", false);
        if (withDocumentName) {
            form.setFieldValue(`${field.name}Name`, "", false);
        }
        onChange("");

        dispatch(thirdPaymentActions.payrollDocumentDelete(fileId));
    };

    /**
     * Estado para no mostrar la notificación en caso de haberse cargado el documento previamente
     * y regresar a la pantalla
     */
    const [documentNotificationDisabled, setDocumentNotificationDisabled] = useState(true);

    useEffect(() => {
        if (defaultDocument?.documentId) {
            setDocumentUploading(defaultDocument);
            setDocumentIsUploading(true);
            setLastDocumentToUploadRequest({
                status: "SUCCESS",
                value: { documentId: defaultDocument.documentId },
                error: null,
            });
        }
    }, [defaultDocument?.documentId]);

    useEffect(() => {
        if (documentToUploadRequestId === uploaderRequestId) {
            setLastDocumentToUploadRequest({
                status: documentToUploadRequestStatus,
                value: documentToUploadRequestValue,
                error: documentToUploadRequestError,
            });
        }
    }, [
        uploaderRequestId,
        documentToUploadRequestId,
        documentToUploadRequestStatus,
        documentToUploadRequestValue,
        documentToUploadRequestError,
    ]);

    useEffect(() => {
        if (lastDocumentToUploadRequestStatus === "SUCCESS") {
            setHasInvalidType(false);
            form.setFieldValue(field.name, lastDocumentToUploadRequestValue?.documentId, false);
            if (withDocumentName) {
                form.setFieldValue(`${field.name}Name`, documentUploading?.documentName, false);
            }
            onChange({ ...documentUploading, documentId: lastDocumentToUploadRequestValue?.documentId });
            setDocument({ ...documentUploading, documentId: lastDocumentToUploadRequestValue?.documentId });

            if (!documentNotificationDisabled) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get("", "Archivo cargado correctamente."),
                        "success",
                        ["ComexNotification", "StepComexClearance"],
                    ),
                );
            }
            dispatch(comexActions.resetDocumentToUploadRequest());
            setDocumentIsUploading(false);
        } else if (lastDocumentToUploadRequestStatus === "FAILURE") {
            setHasInvalidType(false);
            setDocument(documentUploading);
            dispatch(
                notificationActions.showNotification(
                    i18nUtils.get(
                        "",
                        lastDocumentToUploadRequestError ||
                            "Formato de archivo invalido. Por favor, ingresarlo en función de las instrucciones provistas.",
                    ),
                    "error",
                    ["ComexNotification", "StepComexClearance"],
                ),
            );
            setDocumentIsUploading(false);
        }
    }, [lastDocumentToUploadRequestStatus, lastDocumentToUploadRequestValue, lastDocumentToUploadRequestError]);

    useEffect(() => {
        if (!isValid && !alreadyResetDocument) {
            setLastDocumentToUploadRequest({
                status: "INITIAL",
                value: "",
                error: null,
            });
            setHasInvalidType(false);
            setDocument({});
            form.setFieldValue(field.name, "", false);
            if (withDocumentName) {
                form.setFieldValue(`${field.name}Name`, "", false);
            }
            onChange("");
            setAlreadyResetDocument(true);
        } else if (isValid && alreadyResetDocument) {
            setAlreadyResetDocument(false);
        }
    }, [isValid, alreadyResetDocument, form, field]);

    useEffect(() => {
        if (externalUploadError) {
            setUploadError(externalUploadError.split("|")[0]);
            setDocument({
                documentCode: '',
                documentName: '',
                size: 0,
                type: '',
            });
            setDocumentIsUploading(false);
            setHasInvalidType(true);
        }  
    }, [externalUploadError]);

    const uploadFile = () => {
        if (!documentIsUploading) {
            documentInputRef.current.click();
            form.setFieldTouched(field.name);
        }
    };

    const getBase64 = (nativeDocument) =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(nativeDocument);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });

    const onChangeDocument = async (event) => {
        event.stopPropagation();
        event.preventDefault();
        setDocumentNotificationDisabled(!documentNotificationDisabled);
        const newDocument = event.target.files[0];
        if (newDocument) {
            const allowedFileExtensions = allowedTypes || config.get("files.allowedFileExtensions");
            const documentExtensionSliced = `${newDocument.name.split(".").slice(-1)}`;
            const documentExtension = documentExtensionSliced?.toLowerCase();
            const sanitizedName = overrideDocumentName
                ? `${overrideDocumentName}.${documentExtension}`
                : newDocument.name;

            setUploadError(null);
            setHasInvalidType(false);

            
            if (!allowedFileExtensions.includes(documentExtension)) {
                setDocument({
                    documentCode,
                    documentName: newDocument.name,
                    size: newDocument.size,
                    type: documentExtension,
                });
                setHasInvalidType(true);
                setUploadError(i18nUtils.get(`${idForm}.format.error`));
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(
                            `${idForm}.format.error`),
                        "error",
                        ["PayrollNotification"],
                    ),
                );
                return;
            }

            if (newDocument.size > MAX_FILE_SIZE) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(`${idForm}.size.error`),
                        "error",
                        ["PayrollNotification"],
                    ),
                );
                setHasInvalidType(true);
                setUploadError(i18nUtils.get(`${idForm}.input.size.error`));
                setDocument({
                    documentCode,
                    documentName: sanitizedName,
                    size: newDocument.size,
                    type: documentExtension,
                });
                return;
            }

           const invalidFileName = !validarNombreArchivo(newDocument.name.split(".")[0], environmentCuit);

           if (invalidFileName) {
                dispatch(
                    notificationActions.showNotification(
                        i18nUtils.get(`${idForm}.filename.error`),
                        "error",
                        ["PayrollNotification"],
                    ),
                );
                setHasInvalidType(true);
                setUploadError(i18nUtils.get(`${idForm}.filename.error`));
                setDocument({
                    documentCode,
                    documentName: sanitizedName,
                    size: newDocument.size,
                    type: documentExtension,
                });
                return;
            }
            
         
            dispatch(thirdPaymentActions.setPayrollUploadError(null));

            const extractData = (base64String) =>
                base64String
                    .replace("data:image/", "")
                    .split(";")[1]
                    .replace("base64,", "");

            getBase64(newDocument).then((base64) => {
                const documentToUpload = {
                    uploaderRequestId,
                    documentName: sanitizedName,
                    _documentToSave: extractData(base64),
                };
                setDocumentIsUploading(true);
                setDocumentUploading({
                    ...documentToUpload,
                    size: newDocument.size,
                    type: documentExtension,
                });

                dispatch(thirdPaymentActions.payrollUploadDocument(documentToUpload));
            });
        }

        documentInputRef.current.value = "";
    };

    function validarCuit(cuit) {
        if (!/^\d{11}$/.test(cuit)) return false;

        const pesos = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
        const digitos = cuit.split('').map(Number);

        const suma = pesos.reduce((acc, peso, i) => acc + peso * digitos[i], 0);
        const mod = suma % 11;
        let verificador;

        if (mod === 0) verificador = 0;
        else if (mod === 1) verificador = 9;
        else verificador = 11 - mod;

        return digitos[10] === verificador;
    }

    function validarNombreArchivo(nombre, environmentCuit) {

        // Formato de archivo: Sueldos_[AAAAMMDD]_[CUIT]_[NUMERACION]

        const regex = /^Sueldos_(\d{4})(\d{2})(\d{2})_(\d{11})_(\d{3})$/;
        const match = nombre.match(regex);

        if (!match) return false;

        const [,  anio, mes, dia] = match;
        const fechaStr = `${anio}-${mes}-${dia}`;
        const fecha = new Date(fechaStr);

        if (isNaN(fecha.getTime())) return false;

        const cuit = nombre.substring(17, 28);
        
        // Valida que el cuit sea valido
        if (!validarCuit(cuit)) return false;

        // Valida que el nombre del archivo contenga el cuit del ambiente
        if (cuit !=environmentCuit) return false;

        return true;
    }
  

    const hasError = form.touched[field.name] && form.errors[field.name];

     const deleteButton = () => (<Button
                                onClick={handleDelete}
                                className="btn btn-outline uploader-delete-btn"
                                label="global.delete"
                                image="images/trash.svg"
                                block={false}
                                bsStyle="text"
                            />);

    const uploaderByStatus = () => {
        if (lastDocumentToUploadRequestStatus === "SUCCESS") {
            return (
                 <div>
                <div className="singleline-uploader processing-complete uploader-container">
                    <div>
                        <div>
                            <I18n defaultValue="Carga completa" component="strong" />
                        </div>
                        <I18n defaultValue={`${document?.documentName}`} />
                         {isMobile &&<>
                            <br/><br/>
                            {deleteButton()}</>
                        } 
                    </div>
                     {!isMobile &&
                        deleteButton()
                     } 
                    
                </div>
                 <div>
                  
                   
                </div>
                </div>
                
            );
        }

        if (lastDocumentToUploadRequestStatus === "FAILURE" || hasInvalidType) {
            return (
                <div className="singleline-uploader has-error">
                    <div>
                        <p>
                            <I18n
                                defaultValue={
                                    uploadError ? uploadError : "Error durante la carga"
                                }
                            />
                        </p>
                        <I18n defaultValue={`${document?.documentName}  ${document?.size} bytes.`} />
                    </div>
                    <Button
                        block={false}
                        onClick={uploadFile}
                        className="btn btn-small"
                        bsStyle="text"
                        defaultLabelText={i18nUtils.get("", "Volver a subir")}
                        image="images/figma/icon/upload.svg"
                    />
                     
                </div>
                
            );
        }

        return (
            <div
                className={classNames("form-group singleline-uploader btn-uploader", {
                    "has-error": hasError,
                })}>
                <div
                    className={className !== null ? className : "box transparent box-border-dashed flex"}
                    onClick={uploadFile}>
                    {!documentIsUploading ? (
                        <>
                            <Button
                                className="btn-secondary icon-medium btn-only-icon enphasize uploader-icon"
                                image="images/figma/icon/add.svg"
                                block={false}
                                bsStyle="text"
                                disabled={documentIsUploading}
                            />
                            <Button
                                bsStyle="text"
                                block={false}
                                className="text-left px-6 py-0"
                                disabled={documentIsUploading}>
                                <CustomText text={label} textType="I18N" />
                            </Button>
                        </>
                    ) : (
                        <Spinner />
                    )}
                </div>
              

                {hasError && <FieldError error={form.errors[field.name]} />}
            </div>
        );
    };

    return (
        <div>
            {uploaderByStatus()}
            <input
                id="myInput"
                type="file"
                ref={documentInputRef}
                style={{ display: "none" }}
                onChange={onChangeDocument}
            />
        </div>
    );
};

DocumentUploader.propTypes = {
    label: string.isRequired,
    documentCode: string.isRequired,
    overrideDocumentName: string,
    field: shape({
        onBlur: func,
        onChange: func,
        name: string,
        value: oneOfType([number, string]),
    }).isRequired,
    form: shape({
        errors: shape({}),
        touched: shape({}),
    }).isRequired,
    className: string,
    onChange: func,
    defaultDocument: shape({}),
    isValid: bool,
    allowedTypes: arrayOf(string),
    multiUploaderId: string,
    withDocumentName: bool,
};

DocumentUploader.defaultProps = {
    className: null,
    overrideDocumentName: null,
    onChange: () => {},
    defaultDocument: {},
    isValid: true,
    allowedTypes: null,
    multiUploaderId: null,
    withDocumentName: false,
};

export default DocumentUploader;
